import { useMemo } from "react";
import { useCustom, useApiUrl } from "@refinedev/core";
import type {
  IAlert,
  IJourney,
  IForbiddenZoneHistory,
  IForbiddenZone,
  UnifiedEvent,
} from "../types/events";
import {
  normalizeAlert,
  normalizeJourney,
  normalizeForbiddenZone,
  mergeAndSortEvents,
} from "../utils/unifiedEvents";

// --- Individual source hooks ---

function useActiveAlerts() {
  const apiUrl = useApiUrl();
  return useCustom<{ data: IAlert[]; total: number } | IAlert[]>({
    url: `${apiUrl}/alert-history`,
    method: "get",
    config: {
      query: { limit: 100, sortField: "createdAt", sortOrder: "desc" },
    },
    queryOptions: {
      queryKey: ["monitoring", "alerts", "active"],
      refetchInterval: 5000,
      staleTime: 3000,
    },
  });
}

function useRecentEndedAlerts() {
  const apiUrl = useApiUrl();
  return useCustom<{ data: IAlert[]; total: number } | IAlert[]>({
    url: `${apiUrl}/alert-history`,
    method: "get",
    config: {
      query: { limit: 20, sortField: "createdAt", sortOrder: "desc" },
    },
    queryOptions: {
      queryKey: ["monitoring", "alerts", "ended"],
      refetchInterval: 30000,
      staleTime: 20000,
    },
  });
}

function useActiveJourneys() {
  const apiUrl = useApiUrl();
  return useCustom<{ data: IJourney[]; total: number } | IJourney[]>({
    url: `${apiUrl}/journeys`,
    method: "get",
    config: {
      query: { limit: 100, sortField: "createdAt", sortOrder: "desc", join: "user" },
    },
    queryOptions: {
      queryKey: ["monitoring", "journeys", "active"],
      refetchInterval: 5000,
      staleTime: 3000,
    },
  });
}

function useRecentEndedJourneys() {
  const apiUrl = useApiUrl();
  return useCustom<{ data: IJourney[]; total: number } | IJourney[]>({
    url: `${apiUrl}/journeys`,
    method: "get",
    config: {
      query: { limit: 20, sortField: "createdAt", sortOrder: "desc", join: "user" },
    },
    queryOptions: {
      queryKey: ["monitoring", "journeys", "ended"],
      refetchInterval: 30000,
      staleTime: 20000,
    },
  });
}

function useActiveForbiddenZones() {
  const apiUrl = useApiUrl();
  return useCustom<{ entries: IForbiddenZoneHistory[]; total: number }>({
    url: `${apiUrl}/places/forbidden-zones/history`,
    method: "get",
    config: {
      query: { limit: 100 },
    },
    queryOptions: {
      queryKey: ["monitoring", "forbidden-zones", "active"],
      refetchInterval: 5000,
      staleTime: 3000,
    },
  });
}

function useForbiddenZoneDefinitions() {
  const apiUrl = useApiUrl();
  return useCustom<IForbiddenZone[]>({
    url: `${apiUrl}/places/forbidden-zones`,
    method: "get",
    queryOptions: {
      queryKey: ["monitoring", "forbidden-zone-definitions"],
      refetchInterval: 30000, // Refresh every 30s (zone definitions change rarely)
      staleTime: 20000,
    },
  });
}

// --- Helper to extract array from response ---

function extractArray<T>(response: any): T[] {
  if (!response?.data) return [];
  const d = response.data;
  // Handle both paginated { data: [...] } and raw array responses
  if (Array.isArray(d)) return d;
  if (d.data && Array.isArray(d.data)) return d.data;
  return [];
}

function extractFZArray(response: any): IForbiddenZoneHistory[] {
  if (!response?.data) return [];
  const d = response.data;
  if (d.entries && Array.isArray(d.entries)) return d.entries;
  if (d.data && Array.isArray(d.data)) return d.data;
  if (Array.isArray(d)) return d;
  return [];
}

// --- Composite hook ---

export function useUnifiedMonitoringEvents() {
  const activeAlertsQ = useActiveAlerts();
  const endedAlertsQ = useRecentEndedAlerts();
  const activeJourneysQ = useActiveJourneys();
  const endedJourneysQ = useRecentEndedJourneys();
  const activeFZQ = useActiveForbiddenZones();
  const fzDefinitionsQ = useForbiddenZoneDefinitions();

  const isLoading =
    activeAlertsQ.isLoading ||
    activeJourneysQ.isLoading ||
    activeFZQ.isLoading;

  const allAlerts = useMemo(() => {
    const active = extractArray<IAlert>(activeAlertsQ.data);
    const ended = extractArray<IAlert>(endedAlertsQ.data);
    // Merge and deduplicate by id
    const map = new Map<number, IAlert>();
    for (const a of active) map.set(a.id, a);
    for (const a of ended) if (!map.has(a.id)) map.set(a.id, a);
    return Array.from(map.values());
  }, [activeAlertsQ.data, endedAlertsQ.data]);

  const allJourneys = useMemo(() => {
    const active = extractArray<IJourney>(activeJourneysQ.data);
    const ended = extractArray<IJourney>(endedJourneysQ.data);
    const map = new Map<number, IJourney>();
    for (const j of active) map.set(j.id, j);
    for (const j of ended) if (!map.has(j.id)) map.set(j.id, j);
    return Array.from(map.values());
  }, [activeJourneysQ.data, endedJourneysQ.data]);

  const allForbiddenZones = useMemo(() => {
    return extractFZArray(activeFZQ.data);
  }, [activeFZQ.data]);

  // Normalize and merge
  const { activeEvents, endedEvents } = useMemo(() => {
    const normalized: UnifiedEvent[] = [
      ...allAlerts.map(normalizeAlert),
      ...allJourneys.map(normalizeJourney),
      ...allForbiddenZones.map(normalizeForbiddenZone),
    ];

    const sorted = mergeAndSortEvents(normalized);
    const active = sorted.filter((e) => e.isActive);
    const ended = sorted.filter((e) => !e.isActive);

    return { activeEvents: active, endedEvents: ended };
  }, [allAlerts, allJourneys, allForbiddenZones]);

  const forbiddenZoneDefinitions = useMemo(() => {
    const d = fzDefinitionsQ.data?.data;
    if (Array.isArray(d)) return d;
    return [];
  }, [fzDefinitionsQ.data]);

  return {
    activeEvents,
    endedEvents,
    isLoading,
    alertCount: allAlerts.filter((a) => a.isBroadcasting).length,
    journeyCount: allJourneys.filter((j) => !j.hasEnded).length,
    forbiddenZoneCount: allForbiddenZones.filter((fz) => !fz.exitedAt).length,
    forbiddenZoneDefinitions,
  };
}
